import React, { useEffect, useState, useRef } from 'react';
import { blacklistApi } from '../api';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, Loader2, ShieldBan, Globe, Mail } from 'lucide-react';

export default function Blacklist() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState('');
  const [adding, setAdding] = useState(false);
  const fileRef = useRef(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const data = await blacklistApi.getAll({ page, limit: 50 });
      setItems(data.items);
      setTotal(data.total);
    } catch { toast.error('Failed to load blacklist'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, [page]);

  const handleAdd = async () => {
    if (!value.trim()) return;
    setAdding(true);
    try {
      await blacklistApi.add(value.trim());
      toast.success('Added to blacklist');
      setValue('');
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
    finally { setAdding(false); }
  };

  const handleRemove = async (id) => {
    try {
      await blacklistApi.remove(id);
      toast.success('Removed from blacklist');
      fetch();
    } catch { toast.error('Failed to remove'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.split(/[\r\n,]+/).map(l => l.trim()).filter(Boolean);
    if (!lines.length) { toast.error('No entries found'); return; }
    try {
      const res = await blacklistApi.import(lines);
      toast.success(res.message);
      fetch();
    } catch { toast.error('Import failed'); }
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div data-testid="outreach-blacklist">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-[#202124]">Blacklist</h1>
          <p className="text-sm text-[#5F6368]">{total} entries — emails/domains skipped during sending</p>
        </div>
        <div>
          <input type="file" ref={fileRef} accept=".csv,.txt" onChange={handleImport} className="hidden" />
          <button onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 px-4 py-2 bg-white border border-[#E2E8F0] hover:bg-[#F1F3F4] text-[#202124] rounded-full text-sm font-medium shadow-sm transition-colors" data-testid="import-blacklist-btn">
            <Upload className="w-4 h-4 text-[#5F6368]" /> Import CSV
          </button>
        </div>
      </div>

      {/* Add entry */}
      <div className="flex gap-2 mb-5">
        <input value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          placeholder="Enter email or domain (e.g. spam@example.com or example.com)"
          className="flex-1 bg-[#F1F3F4] border border-transparent rounded-full px-5 py-2.5 text-sm text-[#202124] placeholder:text-[#9AA0A6] focus:outline-none focus:bg-white focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20 transition-colors" data-testid="blacklist-input" />
        <button onClick={handleAdd} disabled={adding || !value.trim()}
          className="flex items-center gap-1.5 px-5 py-2.5 bg-[#D93025] hover:bg-[#C5221F] text-white rounded-full text-sm font-medium disabled:opacity-50 shadow-sm transition-colors" data-testid="blacklist-add-btn">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
        </button>
      </div>

      {/* List */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#1A73E8] mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center">
            <ShieldBan className="w-10 h-10 mx-auto mb-3 text-[#9AA0A6]" />
            <p className="text-[#5F6368] font-medium">No blacklisted entries</p>
          </div>
        ) : (
          <div className="divide-y divide-[#F1F3F4]">
            {items.map((item) => (
              <div key={item.id} className="flex items-center justify-between px-5 py-3 hover:bg-[#F8F9FA] transition-colors group">
                <div className="flex items-center gap-3">
                  {item.type === 'domain' ? <Globe className="w-4 h-4 text-[#EA580C]" /> : <Mail className="w-4 h-4 text-[#D93025]" />}
                  <span className="text-sm text-[#202124] font-mono">{item.value}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${item.type === 'domain' ? 'bg-[#FFF3E0] text-[#EA580C]' : 'bg-[#FCE8E6] text-[#D93025]'}`}>{item.type}</span>
                </div>
                <button onClick={() => handleRemove(item.id)} className="p-1.5 text-[#9AA0A6] hover:text-[#D93025] hover:bg-[#FCE8E6] rounded-full opacity-0 group-hover:opacity-100 transition-all">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
